var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { forwardRef, } from "react";
import cn from "classnames";
import { useIcon } from "@react-md/icon";
import { List } from "@react-md/list";
import { bem } from "@react-md/utils";
import defaultGetItemLabel from "./defaultGetItemLabel";
import defaultGetItemValue from "./defaultGetItemValue";
import defaultTreeItemRenderer from "./defaultTreeItemRenderer";
import useTreeMovement from "./useTreeMovement";
var block = bem("rmd-tree");
var defaultGetItemProps = function () { return undefined; };
/**
 * Creates an accessible tree widget that allows you to show hierarchical data
 * in a list presentation view. This component requires the selection and
 * expansion state to be provided/controlled but you can use the
 * `useTreeItemSelection` and `useTreeItemExpansion` hooks for a great starting
 * point for this functionality.
 */
var Tree = forwardRef(function Tree(// eslint-disable-line @typescript-eslint/no-explicit-any
_a, ref) {
    var id = _a.id, className = _a.className, _b = _a.itemRenderer, itemRenderer = _b === void 0 ? defaultTreeItemRenderer : _b, data = _a.data, _c = _a.multiSelect, multiSelect = _c === void 0 ? false : _c, selectedIds = _a.selectedIds, onItemSelect = _a.onItemSelect, onMultiItemSelect = _a.onMultiItemSelect, expandedIds = _a.expandedIds, onItemExpansion = _a.onItemExpansion, onMultiItemExpansion = _a.onMultiItemExpansion, _d = _a.expanderLeft, expanderLeft = _d === void 0 ? false : _d, propExpanderIcon = _a.expanderIcon, _e = _a.labelKey, labelKey = _e === void 0 ? "name" : _e, _f = _a.valueKey, valueKey = _f === void 0 ? "name" : _f, _g = _a.getItemLabel, getItemLabel = _g === void 0 ? defaultGetItemLabel : _g, _h = _a.getItemValue, getItemValue = _h === void 0 ? defaultGetItemValue : _h, _j = _a.getItemProps, getItemProps = _j === void 0 ? defaultGetItemProps : _j, linkComponent = _a.linkComponent, sort = _a.sort, _k = _a.rootId, rootId = _k === void 0 ? null : _k, onBlur = _a.onBlur, onFocus = _a.onFocus, onKeyDown = _a.onKeyDown, props = __rest(_a, // eslint-disable-line @typescript-eslint/no-explicit-any
    ["id", "className", "itemRenderer", "data", "multiSelect", "selectedIds", "onItemSelect", "onMultiItemSelect", "expandedIds", "onItemExpansion", "onMultiItemExpansion", "expanderLeft", "expanderIcon", "labelKey", "valueKey", "getItemLabel", "getItemValue", "getItemProps", "linkComponent", "sort", "rootId", "onBlur", "onFocus", "onKeyDown"]);
    var expanderIcon = useIcon("dropdown", propExpanderIcon);
    var _l = useTreeMovement({
        id: id,
        data: data,
        sort: sort,
        rootId: rootId,
        onBlur: onBlur,
        onFocus: onFocus,
        onKeyDown: onKeyDown,
        multiSelect: multiSelect,
        selectedIds: selectedIds,
        onItemSelect: onItemSelect,
        onMultiItemSelect: onMultiItemSelect,
        expandedIds: expandedIds,
        onItemExpansion: onItemExpansion,
        onMultiItemExpansion: onMultiItemExpansion,
        valueKey: valueKey,
        getItemValue: getItemValue,
    }), items = _l.items, activeId = _l.activeId, setActiveId = _l.setActiveId, itemIdRefs = _l.itemIdRefs, handleBlur = _l.handleBlur, handleFocus = _l.handleFocus, handleKeyDown = _l.handleKeyDown;
    var renderChildItems = function (items, depth, parentIndexes) {
        var listSize = items.length;
        return items.map(function (item, index) {
            var _a;
            var itemId = item.itemId, childItems = item.childItems, isCustom = item.isCustom;
            var selected = selectedIds.includes(itemId);
            var expanded = expandedIds.includes(itemId);
            var id = "";
            var ref;
            var visibleIndex = -1;
            if (!isCustom) {
                (_a = itemIdRefs[itemId], id = _a.id, ref = _a.ref, visibleIndex = _a.visibleIndex);
            }
            var focused = id === activeId;
            return itemRenderer({
                key: itemId,
                id: id,
                liRef: ref,
                depth: depth,
                listSize: listSize,
                itemIndex: index,
                selected: selected,
                expanded: expanded,
                focused: focused,
                onClick: function () {
                    setActiveId(itemId);
                    onItemSelect(itemId);
                    if (childItems) {
                        onItemExpansion(itemId, !expanded);
                    }
                },
                renderChildItems: childItems
                    ? function () {
                        return renderChildItems(childItems, depth + 1, __spreadArrays(parentIndexes, [
                            index + 1,
                        ]));
                    }
                    : undefined,
            }, __assign(__assign({}, item), { visibleIndex: visibleIndex }), __assign({ id: id,
                expanderLeft: expanderLeft, expanderIcon: expanderIcon, multiSelect: multiSelect,
                labelKey: labelKey,
                valueKey: valueKey,
                getItemLabel: getItemLabel,
                getItemValue: getItemValue,
                getItemProps: getItemProps,
                linkComponent: linkComponent,
                rootId: rootId }, props));
        });
    };
    return (React.createElement(List, __assign({}, props, { ref: ref, id: id, "aria-activedescendant": activeId, "aria-multiselectable": multiSelect || undefined, role: "tree", tabIndex: 0, className: cn(block(), className), onBlur: handleBlur, onFocus: handleFocus, onKeyDown: handleKeyDown }), renderChildItems(items, 0, [])));
});
if (process.env.NODE_ENV !== "production") {
    try {
        var PropTypes = require("prop-types");
        Tree.propTypes = {
            id: PropTypes.string.isRequired,
            className: PropTypes.string,
            onBlur: PropTypes.func,
            onFocus: PropTypes.func,
            onKeyDown: PropTypes.func,
            "aria-label": PropTypes.string,
            "aria-labelledby": PropTypes.string,
            // TODO: Update to custom prop validation for itemId and parentId
            data: PropTypes.object.isRequired,
            rootId: PropTypes.string,
            sort: PropTypes.func,
            selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
            onItemSelect: PropTypes.func.isRequired,
            onMultiItemSelect: PropTypes.func.isRequired,
            expandedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
            onItemExpansion: PropTypes.func.isRequired,
            onMultiItemExpansion: PropTypes.func.isRequired,
            multiSelect: PropTypes.bool,
            expanderLeft: PropTypes.bool,
            expanderIcon: PropTypes.node,
            itemRenderer: PropTypes.func,
            labelKey: PropTypes.string,
            valueKey: PropTypes.string,
            getItemLabel: PropTypes.func,
            getItemValue: PropTypes.func,
            getItemProps: PropTypes.func,
            linkComponent: PropTypes.oneOfType([
                PropTypes.func,
                PropTypes.object,
                PropTypes.oneOf(["a"]),
            ]),
        };
    }
    catch (e) { }
}
export default Tree;
//# sourceMappingURL=Tree.js.map